import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import { API_ENDPOINTS } from '../../config/api';

const EventTypes = () => {
    const { user } = useAuth();
    const [eventTypes, setEventTypes] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [newTypeName, setNewTypeName] = useState('');
    const [editMode, setEditMode] = useState(false);

    // Admin kontrolü
    useEffect(() => {
        if (user?.email === 'mertdgn35@gmail.com') {
            fetchEventTypes();
        }
    }, [user]);

    if (!user) {
        return null; // Kullanıcı henüz yüklenmediyse
    }

    const fetchEventTypes = async () => {
        try {
            const response = await fetch(API_ENDPOINTS.eventTypes.getAll(), {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            const data = await response.json();
            setEventTypes(data);
        } catch (error) {
            console.error('Etkinlik türleri yüklenirken hata:', error);
        }
    };

    const handleOpenDialog = (type = null) => {
        setSelectedType(type);
        setNewTypeName(type ? type.name : '');
        setEditMode(!!type);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedType(null);
        setNewTypeName('');
        setEditMode(false);
    };

    const handleOpenDeleteDialog = (type) => {
        setSelectedType(type);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedType(null);
    };

    const handleSave = async () => {
        try {
            const url = editMode
                ? API_ENDPOINTS.eventTypes.update(selectedType.id)
                : API_ENDPOINTS.eventTypes.create();
            const method = editMode ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ name: newTypeName }),
            });

            if (response.ok) {
                fetchEventTypes();
                handleCloseDialog();
            }
        } catch (error) {
            console.error('Etkinlik türü kaydedilirken hata:', error);
        }
    };

    const handleDelete = async () => {
        try {
            const response = await fetch(API_ENDPOINTS.eventTypes.delete(selectedType.id), {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });

            if (response.ok) {
                fetchEventTypes();
                handleCloseDeleteDialog();
            }
        } catch (error) {
            console.error('Etkinlik türü silinirken hata:', error);
        }
    };

    const handleReorder = async (id, direction) => {
        const currentType = eventTypes.find(type => type.id === id);
        const newOrder = direction === 'up'
            ? currentType.order_number - 1
            : currentType.order_number + 1;

        try {
            const response = await fetch(API_ENDPOINTS.eventTypes.order(id), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ order_number: newOrder }),
            });

            if (response.ok) {
                fetchEventTypes();
            }
        } catch (error) {
            console.error('Sıralama güncellenirken hata:', error);
        }
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            {user.email !== 'mertdgn35@gmail.com' ? (
                <Typography variant="h5" sx={{ mt: 4 }}>
                    Bu sayfaya erişim yetkiniz bulunmamaktadır.
                </Typography>
            ) : (
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h4">Etkinlik Türleri Yönetimi</Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                        >
                            Yeni Ekle
                        </Button>
                    </Box>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sıra</TableCell>
                                    <TableCell>Ad</TableCell>
                                    <TableCell align="right">İşlemler</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {eventTypes.map((type, index) => (
                                    <TableRow key={type.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{type.name}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={() => handleReorder(type.id, 'up')}
                                                disabled={index === 0}
                                            >
                                                <ArrowUpIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleReorder(type.id, 'down')}
                                                disabled={index === eventTypes.length - 1}
                                            >
                                                <ArrowDownIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleOpenDialog(type)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleOpenDeleteDialog(type)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Ekleme/Düzenleme Dialog */}
                    <Dialog open={openDialog} onClose={handleCloseDialog}>
                        <DialogTitle>
                            {editMode ? 'Etkinlik Türü Düzenle' : 'Yeni Etkinlik Türü'}
                        </DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Etkinlik Türü Adı"
                                fullWidth
                                value={newTypeName}
                                onChange={(e) => setNewTypeName(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>İptal</Button>
                            <Button onClick={handleSave} variant="contained">
                                Kaydet
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Silme Onay Dialog */}
                    <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                        <DialogTitle>Etkinlik Türünü Sil</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                "{selectedType?.name}" etkinlik türünü silmek istediğinizden emin misiniz?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDeleteDialog}>İptal</Button>
                            <Button onClick={handleDelete} color="error" variant="contained">
                                Sil
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Container>
    );
};

export default EventTypes;